import {
  RULE_BOX_HISTORY_OUTCOMES,
  RULE_BOX_HISTORY_TRIGGERS,
  type RuleBoxHistoryChanges,
} from "@loot-os/api-contract";
import { sql } from "drizzle-orm";
import {
  boolean,
  check,
  index,
  integer,
  jsonb,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  uuid,
} from "drizzle-orm/pg-core";

import { boxes, boxOpenerStyles, endUsers, environments, openGroups } from "./app.js";
import { tenant } from "./auth.js";
import { catalogItems } from "./fulfillment/catalog-item.js";
import { orders } from "./fulfillment/order.js";

// ---- shopify ----------------------------------------------------------------------------------
// Loot Boxes for Shopify: a merchant's shop sells an environment's boxes as Shopify products, and
// the box's value is held here until the shopper reveals it (docs/shopify.md).

export const SHOPIFY_SHOP_STATUSES = ["unlinked", "active", "uninstalled", "redacted"] as const;
export type ShopifyShopStatus = (typeof SHOPIFY_SHOP_STATUSES)[number];

/**
 * The shop's app subscription as Loot OS last read it from Shopify. `none`: never asked;
 * `pending`: the merchant was sent to approve it; `active`: usage charges apply; the rest are
 * Shopify's own end states. Only `active` may sell boxes.
 */
export const SHOPIFY_BILLING_STATUSES = [
  "none",
  "pending",
  "active",
  "declined",
  "cancelled",
  "frozen",
  "expired",
] as const;
export type ShopifyBillingStatus = (typeof SHOPIFY_BILLING_STATUSES)[number];

/**
 * One installed Shopify shop, linked to at most one environment. Writer: the install flow (token
 * exchange, then `provisionShopTenant`), `app/uninstalled` and `shop/redact`; the `billing_*`
 * columns by the billing flow and the `app_subscriptions/*` webhooks;
 * `opener_theme` alone by `setShopOpenerTheme` (a tenant admin on the dashboard's Shopify page, or
 * the merchant in the embedded admin). `access_token_ct` and `refresh_token_ct` must never reach a
 * tenant-facing response.
 */
export const shopifyShops = pgTable(
  "shopify_shops",
  {
    id: text("id").primaryKey(), // ulid
    /** `{name}.myshopify.com`. */
    shopDomain: text("shop_domain").notNull(),
    tenantId: text("tenant_id").references(() => tenant.id, { onDelete: "cascade" }),
    environmentId: text("environment_id").references(() => environments.id, {
      onDelete: "cascade",
    }),
    status: text("status", { enum: SHOPIFY_SHOP_STATUSES }).notNull().default("unlinked"),
    scopes: text("scopes").array().notNull().default(sql`'{}'::text[]`),
    /** AES-256-GCM, key-id prefixed (`encryptToken`). Never tenant-facing. */
    accessTokenCt: text("access_token_ct"),
    accessTokenExpiresAt: timestamp("access_token_expires_at", { withTimezone: true }),
    /** AES-256-GCM, key-id prefixed. Never tenant-facing. */
    refreshTokenCt: text("refresh_token_ct"),
    refreshTokenExpiresAt: timestamp("refresh_token_expires_at", { withTimezone: true }),
    currency: text("currency"),
    onlineStorePublicationGid: text("online_store_publication_gid"),
    /**
     * The shop's store credit product (`ensureTopUpProduct`): one $1 variant a shopper buys any
     * number of at checkout to top their store credit up before a claim. NULL until the first
     * shopper needs it. Written by Loot OS only.
     */
    topUpProductGid: text("top_up_product_gid"),
    topUpVariantGid: text("top_up_variant_gid"),
    brandName: text("brand_name"),
    brandLogoUrl: text("brand_logo_url"),
    brandColor: text("brand_color"),
    /**
     * The opener's theme in the storefront overlay (`ShopifyOpenerTheme`, api-contract), read only
     * through `parseShopifyOpenerTheme`. NULL = the opener's own palette with the brand color as
     * its accent.
     */
    openerTheme: jsonb("opener_theme"),
    /**
     * The opener style this shop's boxes open with in the overlay unless a box names its own.
     * NULL = the environment's. Written by `setShopOpenerStyle` from both editors.
     */
    openerStyleId: text("opener_style_id").references(() => boxOpenerStyles.id),
    lastCatalogSyncAt: timestamp("last_catalog_sync_at", { withTimezone: true }),
    /** The app subscription's state (`SHOPIFY_BILLING_STATUSES`); only `active` sells boxes. */
    billingStatus: text("billing_status", { enum: SHOPIFY_BILLING_STATUSES })
      .notNull()
      .default("none"),
    /** `gid://shopify/AppSubscription/…` the merchant was last asked to approve. */
    billingSubscriptionGid: text("billing_subscription_gid"),
    /** The subscription's usage line item, which every usage record names. */
    billingLineItemGid: text("billing_line_item_gid"),
    /**
     * The fee the merchant approved, basis points: what the subscription's terms say. Usage is
     * billed at the lesser of this and `tenant_settings.shopify_platform_share_bps`.
     */
    billingRateBps: integer("billing_rate_bps"),
    /**
     * A subscription the merchant was sent to approve and has not yet (a first subscription, or a
     * new fee rate), with the rate its terms carry. Promoted onto the columns above once Shopify
     * reports it active; a decline clears it and leaves the current subscription billing.
     */
    billingPendingSubscriptionGid: text("billing_pending_subscription_gid"),
    billingPendingRateBps: integer("billing_pending_rate_bps"),
    /** The subscription's capped amount per 30 days, USD cents, as Shopify last reported it. */
    billingCappedAmountCents: integer("billing_capped_amount_cents"),
    /**
     * When `app_subscriptions/approaching_capped_amount` last arrived (90% of the cap billed).
     * Cleared once Shopify reports the cycle's balance under 90% of the cap again: a raised cap,
     * or a new 30-day cycle (`reconcileBilling`).
     */
    billingCapWarnedAt: timestamp("billing_cap_warned_at", { withTimezone: true }),
    /**
     * When Shopify last refused a usage charge for the cap (`applyUsageCharge`). The shop's charges
     * wait, unbilled and never failed, until it is cleared: by a sync that finds a raised cap or a
     * new subscription (`reconcileBilling`), or by a waiting charge that bills (the retry pass
     * probes one each pass, which is how a new 30-day cycle is found).
     */
    billingCapReachedAt: timestamp("billing_cap_reached_at", { withTimezone: true }),
    /**
     * The app-owned delivery profile every box product of the shop is placed in: one zone over
     * every country with a free rate, so a box adds no shipping at checkout while checkout still
     * collects the address its item ships to. Written by `placeUnplacedBoxProducts`
     * (`functions/src/shared/shopify/boxShipping.ts`) on the first placement, and again when
     * Shopify no longer has it. Shopify keeps one profile per name, so a create whose name is
     * taken tries the next ("Loot Boxes 2"); the app reads no profile but this one.
     */
    boxDeliveryProfileGid: text("box_delivery_profile_gid"),
    /**
     * When the shop's latest token exchange was asked for (`recordInstall`): the install, a
     * reinstall, or a renewal. An `app/uninstalled` or `shop/redact` triggered before it is about
     * an earlier install and is ignored.
     */
    installedAt: timestamp("installed_at", { withTimezone: true }).notNull().defaultNow(),
    linkedAt: timestamp("linked_at", { withTimezone: true }),
    uninstalledAt: timestamp("uninstalled_at", { withTimezone: true }),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    uniqueIndex("shopify_shops_domain_idx").on(t.shopDomain),
    uniqueIndex("shopify_shops_env_idx")
      .on(t.environmentId)
      .where(sql`${t.environmentId} IS NOT NULL`),
    index("shopify_shops_tenant_idx").on(t.tenantId),
    check(
      "shopify_shops_status_check",
      sql`${t.status} IN ('unlinked','active','uninstalled','redacted')`,
    ),
    check("shopify_shops_link_pair", sql`(${t.tenantId} IS NULL) = (${t.environmentId} IS NULL)`),
    check(
      "shopify_shops_active_is_linked",
      sql`${t.status} <> 'active' OR ${t.environmentId} IS NOT NULL`,
    ),
    check(
      "shopify_shops_billing_status_check",
      sql`${t.billingStatus} IN ('none','pending','active','declined','cancelled','frozen','expired')`,
    ),
  ],
);

/**
 * Retired: a one-time code a tenant admin created and a merchant consumed in the embedded admin to
 * link their shop. Shops now provision their own tenant on install (`provisionShopTenant`), so
 * nothing writes it; a later contract migration drops it. `code_hash` must never reach a
 * tenant-facing response.
 */
export const shopifyLinkCodes = pgTable(
  "shopify_link_codes",
  {
    id: text("id").primaryKey(), // ulid
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenant.id, { onDelete: "cascade" }),
    environmentId: text("environment_id")
      .notNull()
      .references(() => environments.id, { onDelete: "cascade" }),
    /** SHA-256 of the code. Never tenant-facing. */
    codeHash: text("code_hash").notNull(),
    /** The dashboard user who created it. */
    createdBy: text("created_by").notNull(),
    expiresAt: timestamp("expires_at", { withTimezone: true }).notNull(),
    consumedAt: timestamp("consumed_at", { withTimezone: true }),
    consumedByShopId: text("consumed_by_shop_id").references(() => shopifyShops.id, {
      onDelete: "set null",
    }),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    uniqueIndex("shopify_link_codes_hash_idx").on(t.codeHash),
    index("shopify_link_codes_env_idx").on(t.environmentId, t.createdAt),
  ],
);

export const SHOPIFY_PRODUCT_STATUSES = ["ACTIVE", "DRAFT", "ARCHIVED"] as const;
export type ShopifyProductStatus = (typeof SHOPIFY_PRODUCT_STATUSES)[number];

/**
 * The Shopify side of a synced variant, one-to-one with a tenant-owned `catalog_items` row.
 * Writer: the catalog sync (the paged import, and the `products/*`, `collections/*` and
 * `inventory_levels/*` webhooks). The product's tags, vendor, type, status, collections and
 * category attributes are copied onto each of its variants, beside the variant's own options and
 * locations, because a rule box's rules match on them.
 */
export const shopifyVariants = pgTable(
  "shopify_variants",
  {
    id: text("id").primaryKey(), // ulid
    shopId: text("shop_id")
      .notNull()
      .references(() => shopifyShops.id, { onDelete: "cascade" }),
    variantGid: text("variant_gid").notNull(),
    productGid: text("product_gid").notNull(),
    inventoryItemGid: text("inventory_item_gid"),
    catalogItemId: uuid("catalog_item_id")
      .notNull()
      .references(() => catalogItems.id, { onDelete: "cascade" }),
    sku: text("sku"),
    /** "Product / Variant". */
    title: text("title").notNull(),
    tracked: boolean("tracked").notNull(),
    priceCents: integer("price_cents").notNull(),
    /** The product's tags, as Shopify has them. */
    productTags: text("product_tags").array().notNull().default(sql`'{}'::text[]`),
    /** The product's vendor; null when blank. */
    vendor: text("vendor"),
    /** The product's type; null when blank. */
    productType: text("product_type"),
    /** The product's status; only an ACTIVE product goes in a rule box. */
    productStatus: text("product_status", { enum: SHOPIFY_PRODUCT_STATUSES })
      .notNull()
      .default("ACTIVE"),
    /** The collections holding the product (`gid://shopify/Collection/…`). */
    collectionGids: text("collection_gids").array().notNull().default(sql`'{}'::text[]`),
    /**
     * The locations stocking the variant: every location its inventory item has a level at, the
     * merchant's own and fulfillment apps'. Readable without `read_locations`, which only names them.
     */
    locationGids: text("location_gids").array().notNull().default(sql`'{}'::text[]`),
    /**
     * The variant's options, `[{ name, value }]` (Size: M), without Shopify's "Title: Default
     * Title" placeholder. Read only through `parseVariantOptions`.
     */
    options: jsonb("options").notNull().default(sql`'[]'::jsonb`),
    /**
     * The product's category attribute values, `{ key: metaobjectGid[] }` from its `shopify.*`
     * category metafields. Synced only while the shop grants `read_metaobjects`, and left as it
     * was otherwise. Read only through `parseCategoryAttributes`.
     */
    categoryAttributes: jsonb("category_attributes").notNull().default(sql`'{}'::jsonb`),
    syncedAt: timestamp("synced_at", { withTimezone: true }).notNull().defaultNow(),
    deletedAt: timestamp("deleted_at", { withTimezone: true }),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    uniqueIndex("shopify_variants_gid_idx").on(t.variantGid),
    uniqueIndex("shopify_variants_catalog_item_idx").on(t.catalogItemId),
    index("shopify_variants_shop_product_idx").on(t.shopId, t.productGid),
    index("shopify_variants_inventory_item_idx").on(t.inventoryItemGid),
    check("shopify_variants_price_nonneg", sql`${t.priceCents} >= 0`),
  ],
);

export const SHOPIFY_CATALOG_LABEL_KINDS = [
  "collection",
  "location",
  "category_attribute",
  "category_value",
] as const;
export type ShopifyCatalogLabelKind = (typeof SHOPIFY_CATALOG_LABEL_KINDS)[number];

/**
 * The names of what a rule box's rules hold by id, per shop, so the builder offers them by name:
 * a collection (`ref` its GID), a location (its GID, with the fulfillment app behind it), a
 * category attribute (`ref` its `shopify.*` metafield key) and a category value (`ref` its
 * metaobject GID). Matching never reads this table. Writer: the catalog sync (collections from
 * every product read; locations only while `read_locations` is granted; category names only while
 * `read_metaobjects` is) and the `collections/*` webhooks. Safe to return to tenant members.
 */
export const shopifyCatalogLabels = pgTable(
  "shopify_catalog_labels",
  {
    id: text("id").primaryKey(), // ulid
    shopId: text("shop_id")
      .notNull()
      .references(() => shopifyShops.id, { onDelete: "cascade" }),
    kind: text("kind", { enum: SHOPIFY_CATALOG_LABEL_KINDS }).notNull(),
    ref: text("ref").notNull(),
    label: text("label").notNull(),
    /** A location's fulfillment app (`FulfillmentService.serviceName`); null for the merchant's own. */
    fulfillmentService: text("fulfillment_service"),
    /**
     * A collection whose members changed (`collections/create` or `update`): the dispatch cron
     * re-reads them and clears this, unless a newer change arrived meanwhile.
     */
    membersRequestedAt: timestamp("members_requested_at", { withTimezone: true }),
    syncedAt: timestamp("synced_at", { withTimezone: true }).notNull().defaultNow(),
    /** Gone in Shopify: a deleted collection, a deactivated location. Kept so a rule can say so. */
    deletedAt: timestamp("deleted_at", { withTimezone: true }),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    uniqueIndex("shopify_catalog_labels_ref_idx").on(t.shopId, t.kind, t.ref),
    index("shopify_catalog_labels_members_idx")
      .on(t.membersRequestedAt)
      .where(sql`${t.membersRequestedAt} IS NOT NULL`),
    check(
      "shopify_catalog_labels_kind_check",
      sql`${t.kind} IN (${sqlList(SHOPIFY_CATALOG_LABEL_KINDS)})`,
    ),
  ],
);

/** The Shopify product that sells a box. Writer: `syncBoxProduct`. */
export const shopifyBoxProducts = pgTable(
  "shopify_box_products",
  {
    id: text("id").primaryKey(), // ulid
    shopId: text("shop_id")
      .notNull()
      .references(() => shopifyShops.id, { onDelete: "cascade" }),
    boxId: text("box_id")
      .notNull()
      .references(() => boxes.id, { onDelete: "cascade" }),
    productGid: text("product_gid").notNull(),
    variantGid: text("variant_gid").notNull(),
    published: boolean("published").notNull().default(false),
    syncedPriceCents: integer("synced_price_cents").notNull(),
    /** The box art last sent as the product's image; "" = none, NULL = not known (resend it). */
    syncedImageUrl: text("synced_image_url"),
    /**
     * Whether the product was last sent sold out: the box's rule is `unavailable`, so its variant
     * is tracked at zero with inventory policy DENY until a refresh solves again.
     */
    syncedSoldOut: boolean("synced_sold_out").notNull().default(false),
    /**
     * The delivery profile the variant was last placed in (`shopify_shops.box_delivery_profile_gid`
     * when current); NULL = not yet: the shop has not granted `write_shipping`, or the placement
     * failed and `ShopifyDispatchRetry` tries again.
     */
    deliveryProfileGid: text("delivery_profile_gid"),
    syncedAt: timestamp("synced_at", { withTimezone: true }).notNull().defaultNow(),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    uniqueIndex("shopify_box_products_box_idx").on(t.boxId),
    uniqueIndex("shopify_box_products_variant_idx").on(t.variantGid),
    index("shopify_box_products_shop_idx").on(t.shopId),
    check("shopify_box_products_price_positive", sql`${t.syncedPriceCents} > 0`),
  ],
);

export const SHOPIFY_RULE_BOX_REFRESH_STATUSES = ["ok", "unavailable"] as const;

/**
 * A Shopify rule box: the merchant's rules, margin and price mode for one box, whose drop table
 * is generated from the shop's products and rebuilt every day (docs/shopify.md). Every Shopify box
 * has one. Writer: the embedded admin (`shopifyAdmin.boxes.create` / `update`) for `config`, and
 * the refresh (`refreshRuleBox`: the admin's save and refresh, and `ShopifyDispatchRetry`'s daily
 * pass) for the status columns, and the `products/*` and `inventory_levels/update` webhooks
 * (`requestRuleBoxRefresh`) for the refresh request. The whole row is safe to return to tenant
 * members.
 */
export const shopifyRuleBoxes = pgTable(
  "shopify_rule_boxes",
  {
    boxId: text("box_id")
      .primaryKey()
      .references(() => boxes.id, { onDelete: "cascade" }),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenant.id, { onDelete: "cascade" }),
    environmentId: text("environment_id")
      .notNull()
      .references(() => environments.id, { onDelete: "cascade" }),
    shopId: text("shop_id")
      .notNull()
      .references(() => shopifyShops.id, { onDelete: "cascade" }),
    /** `RuleBoxConfig` (api-contract), read only through `parseRuleBoxConfig`. */
    config: jsonb("config").notNull(),
    /** The last refresh: `ok`, or `unavailable` with the solver's refusal. */
    refreshStatus: text("refresh_status", { enum: SHOPIFY_RULE_BOX_REFRESH_STATUSES })
      .notNull()
      .default("ok"),
    /** `RuleBoxRefusal` while `unavailable`. */
    refusal: text("refusal"),
    refusalMessage: text("refusal_message"),
    /** The last refresh that solved (whether or not it wrote a version). */
    refreshedAt: timestamp("refreshed_at", { withTimezone: true }),
    /** The last refresh attempted, solved or refused; what the daily due check reads. */
    attemptedAt: timestamp("attempted_at", { withTimezone: true }),
    /**
     * When a webhook last asked for a refresh (a product sold out or came back, or a product
     * changed). The cron runs it when it is after `attempted_at`, and the refresh clears it.
     */
    refreshRequestedAt: timestamp("refresh_requested_at", { withTimezone: true }),
    /**
     * `{ trigger: "stock_change" | "product_change", cause }` for the pending request, read only
     * through `parseRuleBoxRefreshRequest`.
     */
    refreshRequest: jsonb("refresh_request"),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    index("shopify_rule_boxes_env_idx").on(t.environmentId),
    index("shopify_rule_boxes_shop_idx").on(t.shopId),
    check("shopify_rule_boxes_status_check", sql`${t.refreshStatus} IN ('ok','unavailable')`),
  ],
);

export const RULE_BOX_HISTORY_ACTOR_KINDS = ["merchant", "staff", "system"] as const;
export type RuleBoxHistoryActorKind = (typeof RULE_BOX_HISTORY_ACTOR_KINDS)[number];

function sqlList(values: readonly string[]) {
  return sql.raw(values.map((v) => `'${v}'`).join(","));
}

/**
 * A rule box's history: one row per refresh that published, refused, or (for the merchant's and
 * the daily refreshes) ran and changed nothing, naming what set it off and what moved in the
 * drop table (docs/shopify.md), plus one row per rename. Append-only: written by the refresh
 * (`refreshRuleBox`) and a rename (`recordRuleBoxRename`) alone, never updated or deleted (a box's
 * rows go with the box). Readers: tenant staff (the dashboard's `shopify.boxHistory`) and the
 * shop's merchant (`shopifyAdmin.boxes.history`); every column is safe to return to both.
 */
export const shopifyRuleBoxEvents = pgTable(
  "shopify_rule_box_events",
  {
    id: text("id").primaryKey(), // ulid
    boxId: text("box_id")
      .notNull()
      .references(() => boxes.id, { onDelete: "cascade" }),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenant.id, { onDelete: "cascade" }),
    environmentId: text("environment_id")
      .notNull()
      .references(() => environments.id, { onDelete: "cascade" }),
    /** `RuleBoxHistoryTrigger`. */
    trigger: text("trigger", { enum: RULE_BOX_HISTORY_TRIGGERS }).notNull(),
    /** `RuleBoxHistoryOutcome`. */
    outcome: text("outcome", { enum: RULE_BOX_HISTORY_OUTCOMES }).notNull(),
    actorKind: text("actor_kind", { enum: RULE_BOX_HISTORY_ACTOR_KINDS }).notNull(),
    /** The merchant's Shopify user id (or the shop's domain); null for the system. */
    actorLabel: text("actor_label"),
    /** The product a webhook named, or the config fields the merchant changed. */
    cause: text("cause"),
    fromVersion: integer("from_version"),
    toVersion: integer("to_version"),
    priceFromCents: integer("price_from_cents"),
    priceToCents: integer("price_to_cents"),
    itemCountFrom: integer("item_count_from"),
    itemCountTo: integer("item_count_to"),
    /** `RuleBoxHistoryChanges`, read only through `parseRuleBoxHistoryChanges`. */
    changes: jsonb("changes")
      .$type<RuleBoxHistoryChanges>()
      .notNull()
      .default(sql`'{"added":[],"removed":[],"revalued":[]}'::jsonb`),
    refusalMessage: text("refusal_message"),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    index("shopify_rule_box_events_box_idx").on(t.boxId, t.createdAt.desc(), t.id.desc()),
    check(
      "shopify_rule_box_events_trigger_check",
      sql`${t.trigger} IN (${sqlList(RULE_BOX_HISTORY_TRIGGERS)})`,
    ),
    check(
      "shopify_rule_box_events_outcome_check",
      sql`${t.outcome} IN (${sqlList(RULE_BOX_HISTORY_OUTCOMES)})`,
    ),
    check(
      "shopify_rule_box_events_actor_check",
      sql`${t.actorKind} IN (${sqlList(RULE_BOX_HISTORY_ACTOR_KINDS)})`,
    ),
  ],
);

export const SHOPIFY_BOX_PURCHASE_STATUSES = [
  "processing",
  "unrevealed",
  "converted",
  "revealed",
  "claimed",
  "sold",
  "voided",
] as const;
export type ShopifyBoxPurchaseStatus = (typeof SHOPIFY_BOX_PURCHASE_STATUSES)[number];

/**
 * One unit of a box bought through Shopify checkout: the stored value until the unit is revealed
 * or converted. Writer: `orders/paid` (`recordBoxOrder`), the Thank-you page's reveal-link call
 * (`recordBoxOrder` too, which may arrive before the webhook), `refunds/create`, and the shopper's reveal, convert, claim and sell-back through the app proxy.
 * `customer_gid` and `shipping_address` are the shopper's; `customers/redact` strips them.
 */
export const shopifyBoxPurchases = pgTable(
  "shopify_box_purchases",
  {
    id: text("id").primaryKey(), // ulid
    shopId: text("shop_id")
      .notNull()
      .references(() => shopifyShops.id),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenant.id, { onDelete: "cascade" }),
    environmentId: text("environment_id")
      .notNull()
      .references(() => environments.id, { onDelete: "cascade" }),
    /** NULL only while `status = 'processing'`. */
    endUserId: text("end_user_id").references(() => endUsers.id),
    customerGid: text("customer_gid"),
    boxId: text("box_id")
      .notNull()
      .references(() => boxes.id),
    orderGid: text("order_gid").notNull(),
    /** "#1042". */
    orderName: text("order_name").notNull(),
    lineItemId: text("line_item_id").notNull(),
    /** 0..quantity-1. */
    unitIndex: integer("unit_index").notNull(),
    paidPriceCents: integer("paid_price_cents").notNull(),
    /** Copied from the box order; a claim ships here and nowhere else. */
    shippingAddress: jsonb("shipping_address").$type<Record<string, unknown>>(),
    status: text("status", { enum: SHOPIFY_BOX_PURCHASE_STATUSES }).notNull().default("processing"),
    openGroupId: text("open_group_id").references(() => openGroups.id),
    shipOrderId: uuid("ship_order_id").references(() => orders.id),
    statusChangedAt: timestamp("status_changed_at", { withTimezone: true }).notNull().defaultNow(),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    uniqueIndex("shopify_box_purchases_line_unit_idx").on(t.orderGid, t.lineItemId, t.unitIndex),
    uniqueIndex("shopify_box_purchases_open_group_idx")
      .on(t.openGroupId)
      .where(sql`${t.openGroupId} IS NOT NULL`),
    index("shopify_box_purchases_user_status_idx").on(t.endUserId, t.status, t.createdAt),
    index("shopify_box_purchases_env_created_idx").on(t.environmentId, t.createdAt),
    check(
      "shopify_box_purchases_status_check",
      sql`${t.status} IN ('processing','unrevealed','converted','revealed','claimed','sold','voided')`,
    ),
    check("shopify_box_purchases_price_nonneg", sql`${t.paidPriceCents} >= 0`),
    check("shopify_box_purchases_unit_nonneg", sql`${t.unitIndex} >= 0`),
    check(
      "shopify_box_purchases_user_after_processing",
      sql`${t.status} = 'processing' OR ${t.endUserId} IS NOT NULL`,
    ),
    check(
      "shopify_box_purchases_open_after_reveal",
      sql`${t.status} NOT IN ('revealed','claimed','sold') OR ${t.openGroupId} IS NOT NULL`,
    ),
  ],
);

/**
 * `out` credits the shopper's account; `clawback` debits it, at most what it holds; `payment`
 * debits exactly the amount or nothing, the shopper paying for a revealed item.
 */
export const STORE_CREDIT_TRANSFER_DIRECTIONS = ["out", "clawback", "payment"] as const;
/**
 * `reveal` is the box's paid price credited when it is revealed, `claim_payment` the same price
 * paid back from store credit by the claim or sell-back, `top_up` store credit bought at checkout.
 */
export const STORE_CREDIT_TRANSFER_REASONS = [
  "sell_back",
  "conversion",
  "sweep",
  "refund",
  "out_of_stock",
  "reveal",
  "claim_payment",
  "top_up",
] as const;
export const STORE_CREDIT_TRANSFER_STATUSES = ["pending", "applied", "failed"] as const;
export type StoreCreditTransferReason = (typeof STORE_CREDIT_TRANSFER_REASONS)[number];

/**
 * One store credit movement Loot OS makes on Shopify. Shopify's mutation has no idempotency key,
 * so this row is the idempotency record. Writer: convert, sell-back, sweep, refund and the
 * out-of-stock payout (`sellReturnedBoxUnit`) inside their Loot OS transactions; the reveal's
 * credit and the claim's payment before theirs; a top-up order's `orders/paid`; and the
 * `ShopifyStoreCreditRetry` cron, which never applies a `payment` (only the shopper's claim does).
 */
export const shopifyStoreCreditTransfers = pgTable(
  "shopify_store_credit_transfers",
  {
    id: text("id").primaryKey(), // ulid
    shopId: text("shop_id")
      .notNull()
      .references(() => shopifyShops.id),
    environmentId: text("environment_id")
      .notNull()
      .references(() => environments.id, { onDelete: "cascade" }),
    endUserId: text("end_user_id")
      .notNull()
      .references(() => endUsers.id),
    customerGid: text("customer_gid").notNull(),
    direction: text("direction", { enum: STORE_CREDIT_TRANSFER_DIRECTIONS }).notNull(),
    amountCents: integer("amount_cents").notNull(),
    reason: text("reason", { enum: STORE_CREDIT_TRANSFER_REASONS }).notNull(),
    /** The box purchase id; for a `top_up`, the order line (`{orderGid}#{lineItemId}`). */
    ref: text("ref").notNull(),
    status: text("status", { enum: STORE_CREDIT_TRANSFER_STATUSES }).notNull().default("pending"),
    shopifyTransactionGid: text("shopify_transaction_gid"),
    attempts: integer("attempts").notNull().default(0),
    /**
     * Set by whoever is calling Shopify for this row: the post-commit release and the retry cron
     * both take the lease first, so two callers never credit the same transfer twice.
     */
    leaseExpiresAt: timestamp("lease_expires_at", { withTimezone: true }),
    lastError: text("last_error"),
    appliedAt: timestamp("applied_at", { withTimezone: true }),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    uniqueIndex("sct_ref_reason_idx").on(t.ref, t.reason),
    index("sct_pending_idx").on(t.status, t.createdAt).where(sql`${t.status} <> 'applied'`),
    index("sct_user_idx").on(t.endUserId, t.createdAt),
    check("sct_direction_check", sql`${t.direction} IN ('out','clawback','payment')`),
    check(
      "sct_reason_check",
      sql`${t.reason} IN ('sell_back','conversion','sweep','refund','out_of_stock','reveal','claim_payment','top_up')`,
    ),
    check("sct_status_check", sql`${t.status} IN ('pending','applied','failed')`),
    check("sct_amount_positive", sql`${t.amountCents} > 0`),
    check(
      "sct_applied_has_gid",
      sql`${t.status} <> 'applied' OR ${t.shopifyTransactionGid} IS NOT NULL`,
    ),
  ],
);

export const SHOPIFY_USAGE_CHARGE_STATUSES = ["queued", "applied", "failed", "skipped"] as const;
export type ShopifyUsageChargeStatus = (typeof SHOPIFY_USAGE_CHARGE_STATUSES)[number];

/**
 * Loot Labs' fee on one settled box unit, billed to the merchant as a Shopify usage record on the
 * shop's app subscription (docs/shopify.md, "Install and billing"). One row per purchase, written
 * in the transaction that settles it (`accrueShopifyUsageChargeInTx`), then applied after commit
 * under a lease, like a store credit transfer. `skipped`: nothing to bill (no active
 * subscription, or a zero amount), with the reason in `last_error`. Writer: Loot OS only. A
 * tenant sees every row but its lease on the dashboard's Shopify → Fees page: nothing on it is
 * secret, and `last_error` is Shopify's message, as on a store credit transfer.
 */
export const shopifyUsageCharges = pgTable(
  "shopify_usage_charges",
  {
    id: text("id").primaryKey(), // ulid, also the usage record's idempotency key
    shopId: text("shop_id")
      .notNull()
      .references(() => shopifyShops.id),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenant.id, { onDelete: "cascade" }),
    environmentId: text("environment_id")
      .notNull()
      .references(() => environments.id, { onDelete: "cascade" }),
    /** The box purchase settled; one charge per unit. */
    purchaseId: text("purchase_id")
      .notNull()
      .references(() => shopifyBoxPurchases.id),
    /** The unit's paid price, USD cents. */
    volumeCents: integer("volume_cents").notNull(),
    /** The fee rate billed: the lesser of the tenant's fee and the approved rate. */
    rateBps: integer("rate_bps").notNull(),
    /** `volume_cents × rate_bps / 10000`, rounded to the cent. */
    amountCents: integer("amount_cents").notNull(),
    status: text("status", { enum: SHOPIFY_USAGE_CHARGE_STATUSES }).notNull().default("queued"),
    usageRecordGid: text("usage_record_gid"),
    attempts: integer("attempts").notNull().default(0),
    /** Taken by whoever calls Shopify for the row, so two callers never bill it twice. */
    leaseExpiresAt: timestamp("lease_expires_at", { withTimezone: true }),
    lastError: text("last_error"),
    appliedAt: timestamp("applied_at", { withTimezone: true }),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    uniqueIndex("suc_purchase_idx").on(t.purchaseId),
    index("suc_pending_idx").on(t.status, t.createdAt).where(sql`${t.status} = 'queued'`),
    index("suc_shop_idx").on(t.shopId, t.createdAt),
    check("suc_status_check", sql`${t.status} IN ('queued','applied','failed','skipped')`),
    check("suc_amount_nonnegative", sql`${t.amountCents} >= 0 AND ${t.volumeCents} >= 0`),
    check("suc_applied_has_gid", sql`${t.status} <> 'applied' OR ${t.usageRecordGid} IS NOT NULL`),
  ],
);

/**
 * `out_of_stock` is terminal: Shopify refused the ship order because the won variant had nothing
 * available, the Loot OS order was cancelled and the unit paid out as store credit.
 */
export const SHOPIFY_DISPATCH_STATUSES = ["pending", "created", "failed", "out_of_stock"] as const;

/**
 * The Shopify order created for a claimed unit. Like `orders`, it does not cascade from the
 * tenant: `deleteTenant` purges it with the operations records. Writer: `dispatchShopifyOrder`
 * and its retry cron, and the `fulfillments/create` and `orders/cancelled` webhooks.
 */
export const shopifyOrders = pgTable(
  "shopify_orders",
  {
    id: text("id").primaryKey(), // ulid
    orderId: uuid("order_id")
      .notNull()
      .references(() => orders.id),
    shopId: text("shop_id")
      .notNull()
      .references(() => shopifyShops.id),
    boxPurchaseId: text("box_purchase_id")
      .notNull()
      .references(() => shopifyBoxPurchases.id),
    shopifyOrderGid: text("shopify_order_gid"),
    dispatchStatus: text("dispatch_status", { enum: SHOPIFY_DISPATCH_STATUSES })
      .notNull()
      .default("pending"),
    attempts: integer("attempts").notNull().default(0),
    /** The dispatch lease, as on `shopify_store_credit_transfers`. */
    leaseExpiresAt: timestamp("lease_expires_at", { withTimezone: true }),
    lastError: text("last_error"),
    dispatchedAt: timestamp("dispatched_at", { withTimezone: true }),
    /**
     * When an `out_of_stock` substitution was noted on the box order in Shopify (tag + note), the
     * substitution's last step. Null on an `out_of_stock` row means the retry cron still owes it.
     */
    shopifyAnnotatedAt: timestamp("shopify_annotated_at", { withTimezone: true }),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    uniqueIndex("shopify_orders_order_idx").on(t.orderId),
    uniqueIndex("shopify_orders_purchase_idx").on(t.boxPurchaseId),
    uniqueIndex("shopify_orders_gid_idx")
      .on(t.shopifyOrderGid)
      .where(sql`${t.shopifyOrderGid} IS NOT NULL`),
    index("shopify_orders_retry_idx")
      .on(t.dispatchStatus, t.createdAt)
      .where(sql`${t.dispatchStatus} <> 'created'`),
    check(
      "shopify_orders_status_check",
      sql`${t.dispatchStatus} IN ('pending','created','failed','out_of_stock')`,
    ),
    check(
      "shopify_orders_created_has_gid",
      sql`${t.dispatchStatus} <> 'created' OR ${t.shopifyOrderGid} IS NOT NULL`,
    ),
  ],
);

/**
 * The dedupe record for Shopify's at-least-once webhook delivery. Writer: `/v1/webhooks/shopify`.
 * Never tenant-facing.
 */
export const shopifyWebhookDeliveries = pgTable(
  "shopify_webhook_deliveries",
  {
    /** `X-Shopify-Webhook-Id`. */
    webhookId: text("webhook_id").primaryKey(),
    shopId: text("shop_id").references(() => shopifyShops.id, { onDelete: "cascade" }),
    topic: text("topic").notNull(),
    receivedAt: timestamp("received_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [index("shopify_webhook_deliveries_received_idx").on(t.receivedAt)],
);

export const BOX_CHANNELS = ["shopify"] as const;
export type BoxChannel = (typeof BOX_CHANNELS)[number];

/**
 * Which extra channel a box is sold on. It only ever adds a channel; the hosted storefront is
 * unaffected. Writer: tenant editor (`boxes.setChannel`) and the Shopify admin
 * (`shopifyAdmin.boxes.setEnabled`). The whole row is safe to return to tenant members.
 */
export const boxChannelEnablements = pgTable(
  "box_channel_enablements",
  {
    id: text("id").primaryKey(), // ulid
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenant.id, { onDelete: "cascade" }),
    environmentId: text("environment_id")
      .notNull()
      .references(() => environments.id, { onDelete: "cascade" }),
    boxId: text("box_id")
      .notNull()
      .references(() => boxes.id, { onDelete: "cascade" }),
    channel: text("channel", { enum: BOX_CHANNELS }).notNull(),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    uniqueIndex("box_channel_enablements_env_box_channel_idx").on(
      t.environmentId,
      t.boxId,
      t.channel,
    ),
    check("box_channel_enablements_channel_check", sql`${t.channel} IN ('shopify')`),
  ],
);
